import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session, User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { identify, resetAnalytics, track } from '@/lib/analytics';
import { identifyCrispUser, resetCrisp } from '@/lib/crisp';

// ─── Auth State Machine ─────────────────────────────────────────────────────

export type AuthState =
  | { status: 'pending' }
  | { status: 'unauthenticated' }
  | { status: 'needs-mfa'; user: User }
  | { status: 'authenticated'; user: User; session: Session };

// Boot auth is gated behind Supabase calls that, on a stalled SDK (Safari ITP,
// a hung token refresh, IndexedDB stuck), can NEVER resolve — leaving the
// workspace frozen on its loading screen ("ЗАГРУЗКА РАБОЧЕГО ПРОСТРАНСТВА…")
// until a manual hard refresh. These timeouts + the watchdog below guarantee
// the gate always settles.
const AUTH_CALL_TIMEOUT_MS = 5_000; // per Supabase auth call
const AUTH_WATCHDOG_MS = 12_000; // total boot gate before auto-recovery
const AUTH_RELOAD_GUARD = '__altai_auth_stall_reloaded';

function withAuthTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('auth call timed out')), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/**
 * Discriminated-union auth state that detects MFA requirements.
 * Use this in route guards (ProtectedRoute, MfaRoute, PublicRoute).
 */
export function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'pending' });

  useEffect(() => {
    let cancelled = false;
    let settled = false;

    async function resolve(session: Session | null) {
      if (cancelled) return;
      // The SDK responded — clear any stall-reload guard from a prior boot so
      // a future stall in a later session can still auto-recover.
      try {
        sessionStorage.removeItem(AUTH_RELOAD_GUARD);
      } catch {
        /* storage disabled */
      }

      if (!session) {
        settled = true;
        setState({ status: 'unauthenticated' });
        resetAnalytics();
        resetCrisp();
        // Clear Sentry user scope on logout so subsequent events
        // don't attribute to the last-signed-in account.
        Sentry.setUser(null);
        return;
      }

      // The MFA assurance-level check is a SECONDARY network call. If it stalls
      // (the same Supabase stall that froze the workspace), it must NOT wedge
      // the boot: time it out and proceed as authenticated (the session is
      // already valid; AAL step-up enforcement is the rare case).
      let aal: { currentLevel?: string | null; nextLevel?: string | null } | null = null;
      try {
        const { data } = await withAuthTimeout(
          supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
          AUTH_CALL_TIMEOUT_MS,
        );
        aal = data;
      } catch {
        // Stall or error — assume no step-up required rather than hang.
      }
      if (cancelled) return;
      settled = true;

      if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') {
        setState({ status: 'needs-mfa', user: session.user });
      } else {
        setState({ status: 'authenticated', user: session.user, session });
        identify(session.user.id, {
          email: session.user.email || undefined,
        });
        identifyCrispUser({
          userId: session.user.id,
          email: session.user.email || undefined,
        });
        // Tag every Sentry event with the current user so Issues can
        // be filtered + grouped by who hit them. Without this every
        // event was anonymous and impact was impossible to quantify.
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email || undefined,
        });
      }
    }

    // getSession() with a timeout so a stalled SDK can't await forever. On
    // timeout we rely on onAuthStateChange (Supabase emits INITIAL_SESSION on
    // subscribe) and, failing that, the watchdog below.
    withAuthTimeout(supabase.auth.getSession(), AUTH_CALL_TIMEOUT_MS)
      .then(({ data: { session: s } }) => resolve(s))
      .catch(() => {
        /* stall — onAuthStateChange / watchdog will recover */
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => resolve(s));

    // Watchdog — if nothing settled, the SDK is fully stalled. Automate the
    // hard refresh the user currently does by hand (a fresh page re-inits the
    // SDK and clears the stall). One-shot per session so a genuinely broken
    // deploy can't trap the user in a reload loop; if we already reloaded once,
    // fall back to unauthenticated (→ /signup) rather than an infinite loader.
    const watchdog = setTimeout(() => {
      if (settled || cancelled) return;
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(AUTH_RELOAD_GUARD) === '1';
      } catch {
        /* storage disabled */
      }
      if (!alreadyReloaded && typeof window !== 'undefined') {
        try {
          sessionStorage.setItem(AUTH_RELOAD_GUARD, '1');
        } catch {
          /* storage disabled — still reload */
        }
        window.location.reload();
      } else if (!cancelled) {
        setState({ status: 'unauthenticated' });
      }
    }, AUTH_WATCHDOG_MS);

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      subscription.unsubscribe();
    };
  }, []);

  return state;
}

// ─── Legacy hook — kept for backward compat, delegates to useAuthState ───────

export function useSession() {
  const auth = useAuthState();

  return {
    session: auth.status === 'authenticated' ? auth.session : null,
    user: auth.status === 'authenticated' || auth.status === 'needs-mfa' ? auth.user : null,
    loading: auth.status === 'pending',
    isAuthenticated: auth.status === 'authenticated',
  };
}

// Public registration is closed. The hook is kept so existing call sites
// keep compiling, but it short-circuits to a friendly error instead of
// hitting Supabase. To re-enable signups: remove the early return AND
// flip Supabase Auth → Providers → Email → "Allow new users to sign up"
// back on in the Supabase dashboard.
const REGISTRATION_CLOSED_MESSAGE =
  'Регистрация новых пользователей закрыта. Если у вас уже есть аккаунт, войдите в систему. По вопросам доступа свяжитесь с нами через форму контактов на altailabs.io.';

export function useSignUp() {
  const [loading] = useState(false);
  const [error] = useState<string | null>(REGISTRATION_CLOSED_MESSAGE);

  async function signUp(_email: string, _password: string, _name: string) {
    return false;
  }

  return { signUp, loading, error };
}

export function useSignIn() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsMfa, setNeedsMfa] = useState(false);

  async function signIn(email: string, password: string, captchaToken?: string) {
    setLoading(true);
    setError(null);
    setNeedsMfa(false);

    const { error: e } = await supabase.auth.signInWithPassword({
      email,
      password,
      // Supabase forwards the token to hCaptcha's siteverify using
      // the secret configured in the Supabase dashboard. If the
      // dashboard toggle is off, the field is ignored — safe to
      // always pass.
      options: captchaToken ? { captchaToken } : undefined,
    });
    if (e) {
      setLoading(false);
      setError(e.message);
      // Capture sign-in failures (bad password, captcha fail, locked,
      // etc.) so login-funnel drop-offs are visible. Skipped for
      // well-understood 'Invalid login credentials' noise — those
      // would drown the Issues tab.
      if (!/invalid login credentials/i.test(e.message)) {
        Sentry.captureMessage(`signIn failed: ${e.message}`, {
          level: 'warning',
          tags: { kind: 'auth-signin' },
          extra: { email },
        });
      }
      return false;
    }

    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') {
      setLoading(false);
      setNeedsMfa(true);
      return 'mfa';
    }

    track('login_completed', { email });
    setLoading(false);
    return true;
  }

  return { signIn, loading, error, needsMfa };
}

export function useSignOut() {
  const navigate = useNavigate();

  async function signOut() {
    // Race Supabase signOut against a 3s timeout so a flaky network
    // can't leave the user stuck on the authenticated page. Either way
    // we clear local session artefacts and proceed to the redirect.
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('signOut timeout')), 3000)),
      ]);
    } catch {
      /* Swallow — we still want to sign out locally on timeout/error. */
    }
    try {
      resetAnalytics();
    } catch {
      /* non-fatal */
    }
    try {
      resetCrisp();
    } catch {
      /* non-fatal */
    }
    // Belt-and-suspenders: strip Supabase auth token entries from
    // localStorage directly. supabase.auth.signOut() is supposed to
    // do this but when the call times out it may not complete.
    try {
      if (typeof window !== 'undefined') {
        Object.keys(window.localStorage)
          .filter((k) => k.startsWith('sb-') && k.endsWith('-auth-token'))
          .forEach((k) => window.localStorage.removeItem(k));
      }
    } catch {
      /* quota / blocked storage — ignore */
    }
    // Hard reload — SPA navigate() keeps cached React context alive
    // (query-client, WorkspaceCore provider, Sentry user context).
    // A full page load is the only reliable way to reset every
    // in-memory reference to the just-ended session.
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    } else {
      navigate('/login');
    }
  }

  return { signOut };
}

export function useResetPassword() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function resetPassword(email: string, captchaToken?: string) {
    setLoading(true);
    setError(null);
    const redirectTo = `${window.location.origin}/welcome`;
    const { error: e } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
      captchaToken,
    });
    setLoading(false);
    if (e) {
      setError(e.message);
      // Always capture reset failures — they're rare enough that
      // Sentry noise isn't a concern and the Supabase error text
      // (captcha missing, rate limit, SMTP not configured, etc.) is
      // the fastest way to diagnose "email isn't arriving".
      Sentry.captureMessage(`resetPassword failed: ${e.message}`, {
        level: 'warning',
        tags: { kind: 'auth-reset' },
        extra: { email, redirectTo, hasCaptchaToken: Boolean(captchaToken) },
      });
      return false;
    }
    // Breadcrumb on success too — lets us correlate "no email
    // arrived" reports with whether Supabase even accepted the call
    // (Supabase returns success immediately regardless of whether
    // SMTP actually sends).
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'resetPassword sent',
      level: 'info',
      data: { email, redirectTo },
    });
    setSent(true);
    return true;
  }

  return { resetPassword, loading, error, sent };
}

export function useMfaVerify() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function verify(code: string) {
    setLoading(true);
    setError(null);

    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp?.[0];
    if (!totp) {
      setLoading(false);
      setError('No MFA factor found');
      return false;
    }

    const { data: challenge, error: ce } = await supabase.auth.mfa.challenge({ factorId: totp.id });
    if (ce) {
      setLoading(false);
      setError(ce.message);
      return false;
    }

    const { error: ve } = await supabase.auth.mfa.verify({
      factorId: totp.id,
      challengeId: challenge.id,
      code,
    });
    setLoading(false);
    if (ve) {
      setError(ve.message);
      return false;
    }
    return true;
  }

  return { verify, loading, error };
}
